import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { MdCheck, MdPallet } from 'react-icons/md';
import { Button, Checkbox, Divider, Table, Tooltip, Whisper } from 'rsuite';
import { TableProps } from 'rsuite/lib/Table';
import { TablePaginationProps } from 'rsuite/lib/Table/TablePagination';
import infoIcon from '../../../../../../../assets/icons/icon_info-gray.svg';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger, CurrencyInput, InputAddon, InputGroup } from '../../../../../../../components';
import { LoadingSpinerArea } from '../../../../../../../components/LoadingSpinner';
import { selectorSuppliersExhibitionType, useSuppliersExhibitionStore } from '../../../../../../../store/useSuppliersExhibitionStore';
import { BaseCell } from '../../../../../RevisaoPrecos/Components';
import { UseSelectedDatapointsProps, useUrlPathDetection } from '../../../../hooks';
import { competitorLoadingArea } from '../../NegociacaoFornecedor.rowIdentity';
import { NegotiationDataItemProps, NegotiationSupplierDatapointStatus, NegotitationRowDataProps } from '../../NegociacaoFornecedor.types';
import {
  CostNegotiationMode,
  DEFAULT_COST_NEGOTIATION_MODE,
  ENTRY_COST_UNAVAILABLE_MESSAGE,
  calculateLiquidCostFromEntryCost,
  getCurrentInputCostForNegotiation,
  getCurrentInputCostForVariation,
  getCurrentLiquidCostForNegotiation,
  resolveCostNegotiationModeForRow,
  resolveLiquidCostForNegotiation,
} from '../../costNegotiation';
import { explicacaoDoPrecoDeConcorrencia } from '../../explicacaoDoPrecoDeConcorrencia';
import { ordenaNaVisao } from '../../ordenacaoDaVisaoRede';
import { PMZNewCostCalc, calculateVariationPercentage, competitivenessIndicator, formatDecimal } from '../../utils';
import { ActionButtonsContainer } from '../ActionButtons';
import { BestBuyPriceIndicator } from '../BestBuyPriceIndicator';
import { CostDetailsPopover, buildCurrentCostDetailsRows, buildNewCostDetailsRows } from '../CostDetailsPopover';
import { ExpandedTableContent } from '../ExpandedTableContent';
import { GridErrorState } from '../GridErrorState';
import { MoneyPercentageIndicator } from '../MoneyPercentageIndicator';
import { StoresModal } from '../StoresModal';
import { VariationBoxIndicator } from '../VariationBoxIndicator';
import { VerbaDeSellInCell } from '../VerbaDeSellInCell';
import type { UseVerbaDeSellIn, VerbaParaGravar } from '../../verbaDeSellIn.hooks';
import { LIMPAR_A_VERBA, MENU_DA_VERBA } from '../../verbaDeSellIn';
import { numerosDaVerbaParaADica } from '../../verbaNaDica';
import styles from './TableFornecedor.module.scss';
import { dimensoesDaColuna, layoutDaColuna } from './layoutDasColunas';

const { Column, HeaderCell, Cell, Pagination } = Table;

export type SortTypeProps = 'asc' | 'desc';

type RowDataProps = NegotiationDataItemProps;

/** O detalhe de uma celula, atras de um icone -- e nao ao passar o ponteiro sobre a celula. */
const DetalheNoIcone = ({ speaker, placement = 'auto' }: { speaker: React.ReactElement; placement?: 'auto' | 'top' | 'left' | 'right' }) => (
  <Whisper placement={placement} trigger="hover" speaker={speaker}>
    <img src={infoIcon} className={styles['icone-detalhe']} alt="Ver o detalhe desta coluna" />
  </Whisper>
);

/** Cortar ou quebrar sai de `layoutDasColunas.ts`; cortado, a dica revela o nome inteiro. */
const NomeDoProduto = ({ nome }: { nome: string }) => {
  if (layoutDaColuna('produto').texto !== 'corta') return <p className={styles['produto-name']}>{nome}</p>;

  return (
    <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>{nome}</Tooltip>}>
      <p className={classNames(styles['produto-name'], styles['produto-name--corta'])}>{nome}</p>
    </Whisper>
  );
};

export interface ITableFornecedor extends Omit<TableProps<keyof RowDataProps, RowDataProps>, 'expandedRowKeys'> {
  data?: TableProps['data'];
  expandedRowKeys?: string[];
  pagination?: TablePaginationProps;
  isLoading?: boolean;
  /** A consulta que carrega a grade está em falha. */
  isError?: boolean;
  /** Nova busca da consulta que carrega a grade. */
  onRetry?: () => void;
  selectedDatapoints: UseSelectedDatapointsProps;
  /**
   * A tela que usa esta grade sabe buscar o detalhe de concorrência de uma linha de rede, que exige
   * buscar loja por loja e juntar. Ausente ou falso, o botão de abrir não é oferecido na visão rede:
   * pedir o detalhe sem informar loja mostra ao comprador a concorrência de outra coisa.
   */
  offersNetworkCompetitionDetail?: boolean;
  onExpandRow?: (rowData: RowDataProps) => void;
  onChangeNewCost: (index: number, value: string, rowData: any) => void;
  onChangeNewPrice: (index: number, value: string, rowData: any) => void;
  onChangeObjectiveMargin: (index: number, value: string, rowData: any) => void;
  onBlur: (
    rowData: RowDataProps,
    index: number,
    details: {
      onBlurValue: number | null;
      onFocusValue: number | null;
      hasChanges: boolean;
    },
  ) => void;
  onContextMenu?: (rowData: RowDataProps, index: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onStatusChange?: (newStatus: NegotiationSupplierDatapointStatus, updatedRowData: NegotitationRowDataProps) => void;
  costNegotiationMode?: CostNegotiationMode;
  /** A grade mostra uma negociação fechada. */
  isClosedNegotiation?: boolean;
  disableCheckBoxes?: boolean;
  disableInputs?: boolean;
  contextMenu?: {
    onUndoChanges: (rowData: RowDataProps) => void;
  };
  /** Ausente, a coluna da verba nao e desenhada. */
  verba?: UseVerbaDeSellIn;
  /** Sair do campo nao passa o segundo argumento; aplicar a ideal o PASSA, no mesmo clique. */
  onVerbaBlur?: (rowData: RowDataProps, verbaParaGravar?: VerbaParaGravar | null) => void;
}

const TableFornecedor = React.memo(
  ({
    data,
    pagination,
    isLoading,
    isError,
    onRetry,
    expandedRowKeys,
    rowKey,
    selectedDatapoints,
    offersNetworkCompetitionDetail = false,
    onExpandRow,
    onBlur,
    onChangeObjectiveMargin,
    onChangeNewCost,
    onChangeNewPrice,
    onContextMenu,
    onStatusChange,
    costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE,
    isClosedNegotiation = false,
    disableCheckBoxes = false,
    disableInputs = false,
    contextMenu,
    verba,
    onVerbaBlur,
    ...props
  }: ITableFornecedor) => {
    // Qual campo da linha é a identidade dela vem de fora, na chave que este componente já recebe e
    // já repassa à tabela.
    const rowIdentityKey: keyof RowDataProps = rowKey ?? 'productsToBePricedId';

    const getRowIdentity = (rowData: RowDataProps) => String(rowData?.[rowIdentityKey] ?? '');

    // Sem novo custo digitado NAO ha custo liquido de trabalho, e e isso que desabilita o campo
    // da verba. Recuar para o custo de hoje aceitaria verba sobre custo que ninguem negociou.
    const novoCustoLiquidoDaLinha = (rowData: RowDataProps): number | null => {
      const temNovoCusto = rowData?.newCost !== null && rowData?.newCost !== undefined;
      if (!temNovoCusto) return null;

      return resolveLiquidCostForNegotiation(rowData, rowData.newCost as number, resolveCostNegotiationModeForRow(rowData, costNegotiationMode));
    };

    const campoDaVerbaDaLinha = (rowData: RowDataProps) =>
      verba?.campoDaLinha(getRowIdentity(rowData), rowData, {
        novoCustoLiquido: novoCustoLiquidoDaLinha(rowData),
        negociacaoFechada: disableInputs,
        unidade: verba.unidadeDaLinha(getRowIdentity(rowData)),
      });

    // Com a consulta em falha a grade não tem linha para mostrar, e as que ela tinha antes não valem
    // mais:
    const rows = isError ? [] : data;

    // O bloco não fica na tela durante a nova busca. A tabela real já suprime a mensagem de corpo
    // vazio enquanto o carregamento está ativo, mas a condição está escrita aqui de propósito: assim
    // o comportamento não depende da ordem interna de duas peças de um pacote de terceiro, e pode ser
    // afirmado por caso.
    const shouldShowLoadErrorBlock = Boolean(isError) && !isLoading;

    const hasItemInList = (id: string) => {
      const existInList = expandedRowKeys?.some((item: string) => item === id);
      return existInList;
    };

    const datapointLength = selectedDatapoints.length;

    // Lê a lista que a grade MOSTRA, e não a que ela recebeu:
    const hasRowOnScreen = Boolean(rows?.length);

    const isCheckedAll = hasRowOnScreen && (selectedDatapoints.data.selectedAll ? !datapointLength : datapointLength === rows?.length);

    const isIndeterminate = !isCheckedAll && !!datapointLength;

    const suppliersExhibitionType = useSuppliersExhibitionStore(selectorSuppliersExhibitionType);
    const [showClusterModal, setShowClusterModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<RowDataProps | null>(null);

    // URL path detection for conditional column rendering
    const { isNegociacoesSalvasPath } = useUrlPathDetection();

    const isShowCountStoreCluster = suppliersExhibitionType === 'PRODUCT_SUPPLIERS_CLUSTER';

    const isNetworkExhibition = suppliersExhibitionType === 'PRODUCT_SUPPLIERS_NETWORK';

    // Quais colunas oferecem o controle de ordenação, por coluna e não em bloco.
    const ordenaColuna = (chaveDaColuna: string) => ordenaNaVisao(chaveDaColuna, isNetworkExhibition);

    const handleOpenClusterModal = (rowData: RowDataProps) => {
      setSelectedRowData(rowData);
      setShowClusterModal(true);
    };

    const handleCloseClusterModal = () => {
      setShowClusterModal(false);
      setSelectedRowData(null);
    };

    const isChecked = (id: string) => {
      if (selectedDatapoints.data.selectedAll) {
        return !selectedDatapoints.data.excluded.some(item => item === id);
      }
      return selectedDatapoints.data.selected.some(item => item === id);
    };

    const [contextMenuItem, setContextMenuItem] = useState<null | RowDataProps>(null);

    const handleContextMenu = (rowData: RowDataProps, index: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setContextMenuItem(rowData);
      onContextMenu?.(rowData, index, event);
    };

    const renderCurrentCostTooltip = (
      rowData: RowDataProps,
      rowCostNegotiationMode: CostNegotiationMode,
      currentCost: number,
      currentLiquidCost: number,
      currentPMZ: number,
      isEntryCostUnavailable: boolean,
    ) => {
      return (
        <CostDetailsPopover
          rows={buildCurrentCostDetailsRows({
            costNegotiationMode: rowCostNegotiationMode,
            currentCost,
            currentLiquidCost,
            currentPMZ,
            purchaseTaxPercentage: rowData.purchaseTaxPercentage,
            salesTaxPercentage: rowData.salesTaxPercentage,
            isEntryCostUnavailable,
          })}
        />
      );
    };

    const numerosDaVerbaDaLinha = (rowData: RowDataProps) => numerosDaVerbaParaADica(campoDaVerbaDaLinha(rowData), rowData.effectiveNetCost);

    // Variacao e aliquota NAO vao a dica: a variacao vive no selo da celula, e a aliquota ja
    // chega aplicada dentro de newLiquidCost.
    const renderNewCostTooltip = (
      rowData: RowDataProps,
      rowCostNegotiationMode: CostNegotiationMode,
      newCost: number,
      newLiquidCost: number,
      PMZNewCost: number,
      isEntryCostUnavailable: boolean,
    ) => {
      return (
        <CostDetailsPopover
          rows={buildNewCostDetailsRows({
            costNegotiationMode: rowCostNegotiationMode,
            newCost,
            newLiquidCost,
            PMZNewCost,
            isEntryCostUnavailable,
            ...numerosDaVerbaDaLinha(rowData),
          })}
        />
      );
    };

    return (
      <div className={styles['table-fornecedor-container']}>
        <ContextMenu>
          <ContextMenuTrigger>
            <Table
              // Trocar o campo que nomeia a linha monta uma tabela nova em vez de reaproveitar a
              // anterior, e com isso nada desenhado sob a identidade antiga atravessa para a nova.
              key={rowIdentityKey}
              data={rows}
              loading={isLoading}
              renderEmpty={shouldShowLoadErrorBlock ? () => <GridErrorState onRetry={() => onRetry?.()} /> : undefined}
              autoHeight
              id="table-negociacoes-fornecedor"
              className={styles['table-fornecedor']}
              expandedRowKeys={expandedRowKeys}
              rowKey={rowKey}
              rowClassName={(rowData: RowDataProps) => {
                return classNames(styles['table-fornecedor-row'], `row-data-${getRowIdentity(rowData)}`);
              }}
              rowExpandedHeight={200}
              // A altura cabe o campo e as DUAS linhas de subtexto; mais baixa, a segunda some.
              rowHeight={82}
              headerHeight={46}
              renderRowExpanded={rowData => {
                // A área do indicador de carregamento do detalhe sai da MESMA identidade de linha que
                // a grade usa em tudo o mais, e é a grade que a nomeia: quem busca o detalhe nomeia a
                // área pela mesma função, e assim as duas pontas não podem discordar.
                return <ExpandedTableContent rowData={rowData} loadingArea={competitorLoadingArea(getRowIdentity(rowData))} />;
              }}
              {...props}
            >
              {/* Sem anotação de erro esperado, ao contrário das colunas vizinhas: quando TODAS as
                  propriedades da coluna chegam por espalhamento, o verificador de tipos deixa de
                  reclamar que a coluna recebe filhos, e a anotação seria recusada por não cobrir
                  erro nenhum. Onde a coluna também escreve `sortable` à mão a reclamação continua, e
                  lá a anotação continua necessária. Medido em 05/09/2026. */}
              <Column {...dimensoesDaColuna('seleção')}>
                <HeaderCell style={{ padding: '2px' }}>
                  <Checkbox
                    checked={isCheckedAll}
                    indeterminate={isIndeterminate}
                    onChange={(_v, checked) => {
                      // A chave em uso é informada em vez de o gancho de seleção adivinhar por qual
                      // campo mapear a página.
                      selectedDatapoints.onCheckAll(rows ?? [], checked, rowIdentityKey);
                    }}
                    disabled={disableCheckBoxes}
                  />
                </HeaderCell>
                <Cell>
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <Checkbox
                        value={getRowIdentity(rowData)}
                        checked={isChecked(getRowIdentity(rowData))}
                        onChange={(value, checked) => {
                          selectedDatapoints.onCheck(value, checked);
                        }}
                        disabled={disableCheckBoxes}
                      />
                    </div>
                  )}
                </Cell>
              </Column>
              {/* A largura desta coluna, como a das demais, mora em `layoutDasColunas.ts`.

                  A anotação de erro esperado abaixo tem de ficar COLADA na coluna: um comentário no
                  meio a desliga, e o erro que ela cobre volta a aparecer. */}
              {/* @ts-expect-error coluna não pode receber children */}
              <Column sortable {...dimensoesDaColuna('produto')}>
                <HeaderCell>Produto</HeaderCell>
                {/* As chaves de dado destas colunas são o nome do campo que a tabela entrega ao
                    manipulador de ordenação, e é esse nome que o serviço de fornecedores lê. Ele NÃO
                    recusa nome desconhecido: recua em silêncio para ordenação por identificador de
                    produto. Por isso os nomes aqui são os do vocabulário do serviço, e não os nomes
                    dos campos da linha — cinco colunas desta grade reordenavam por produto por causa
                    dessa diferença. Nesta, o efeito era invisível porque o recuo coincidia com o que a
                    coluna pede. */}
                <Cell dataKey="product_id" height={60}>
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <div className={styles['produto-numbers-wrapper']}>
                        <p className={classNames(styles['produto-first-value'], styles['cell-main-text'])}>{rowData.productId}</p>
                        {rowData.productFamilyId ? (
                          <div className={[styles['blue-box'], 'produto-box'].join(' ')}>
                            <GroupWorkIcon className={styles['produto-second-icon']} />
                            <span className={styles['produto-second-value']}>{rowData.productFamilyId}</span>
                          </div>
                        ) : null}
                      </div>
                      <NomeDoProduto nome={rowData.description} />
                    </div>
                  )}
                </Cell>
              </Column>
              {/* @ts-expect-error coluna não pode receber children */}

              <Column sortable={suppliersExhibitionType !== 'PRODUCT_SUPPLIERS_CLUSTER' && ordenaColuna('store_id')} {...dimensoesDaColuna('loja')}>
                <HeaderCell>{isNetworkExhibition ? 'Rede' : 'Loja'}</HeaderCell>
                {/* A chave é a do código de loja porque é por ela que esta coluna ordena, e ela só
                    oferece ordenação na visão por loja — que é justamente onde a célula mostra o código
                    da loja. Antes a coluna não declarava chave nenhuma, e o serviço leu isso como
                    ordenar por identificador de produto: clicar em "Loja" reordenava por produto. */}
                <Cell dataKey="store_id">
                  {(rowData: RowDataProps, index: number) => (
                    <BaseCell rowData={rowData as any} handleContextMenu={rowData => handleContextMenu?.(rowData as any, index, {} as any)}>
                      {isNetworkExhibition ? (
                        // No grão rede a linha é o produto e não existe nome de localidade a exibir,
                        // então a célula carrega só o contador de lojas.
                        <div className={styles['store-network-count-centered']}>
                          {/* A etiqueta continua clicável quando o contador não chegou. O modal já
                              mostra traço no total de lojas nesse caso, e um produto continua tendo
                              lojas mesmo quando o número não chegou — abrir a lista é honesto,
                              bloquear o clique esconderia dado que existe. */}
                          <span
                            className={styles['store-cluster-count']}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleOpenClusterModal(rowData);
                            }}
                          >
                            {/* Recuo para a marca de dado ausente do módulo quando o contador não
                                chegou: sem ele a célula desenhava a palavra "LOJAS" sozinha, que se
                                lê como rótulo e não como dado faltando. A comparação é contra
                                ausência e não contra veracidade, porque zero é um número de verdade
                                — produto que não tem linha em loja nenhuma conta zero lojas. */}
                            {rowData.network?.storeCount != null ? (
                              <>
                                {rowData.network.storeCount} {rowData.network.storeCount === 1 ? 'LOJA' : 'LOJAS'}
                              </>
                            ) : (
                              '--'
                            )}
                          </span>
                        </div>
                      ) : suppliersExhibitionType === 'PRODUCT_SUPPLIERS_CLUSTER' ? (
                        // Exibição para CLUSTER com fallback para STORE
                        rowData.cluster ? (
                          <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>{rowData.cluster.clusterName}</Tooltip>}>
                            <span className="font-size-100-semibold">
                              {rowData.cluster.clusterName}
                              <br />
                              {isShowCountStoreCluster && (
                                <span
                                  className={styles['store-cluster-count']}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleOpenClusterModal(rowData);
                                  }}
                                >
                                  {rowData?.cluster.clusterItemsCount} {rowData?.cluster.clusterItemsCount === 1 ? 'LOJA' : 'LOJAS'}
                                </span>
                              )}
                            </span>
                          </Whisper>
                        ) : (
                          // Fallback para dados de loja quando não há cluster
                          <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>{rowData.store || rowData.storeName}</Tooltip>}>
                            <div className={styles['store-id-centered']}>{rowData.storeId}</div>
                          </Whisper>
                        )
                      ) : (
                        // Exibição para STORE
                        <Whisper delayShow={0.2} trigger="hover" placement="right" speaker={<Tooltip>{rowData.store || rowData.storeName}</Tooltip>}>
                          <div className={styles['store-id-centered']}>{rowData.storeId}</div>
                        </Whisper>
                      )}
                    </BaseCell>
                  )}
                </Cell>
              </Column>
              {/* @ts-expect-error coluna não pode receber children */}
              <Column sortable={ordenaColuna('stock_quantity')} {...dimensoesDaColuna('estoque')}>
                <HeaderCell>Estoque</HeaderCell>
                <Cell dataKey="stock_quantity">
                  {(rowData: RowDataProps) => (
                    <div className={styles['cost-indicator-cell']}>
                      {!rowData.stock ? (
                        <span className={styles['placeholder']}>--</span>
                      ) : (
                        <div className={styles['estoque-venda-container']}>
                          <div className={styles['estoque-venda-top']}>
                            <span className={styles['estoque-venda-icon']}>
                              <MdPallet width={10} height={10} />
                            </span>
                            <Whisper placement="top" speaker={<Tooltip>Estoque</Tooltip>}>
                              <span className={styles['estoque-venda-numero']}>{rowData.stock.quantity}</span>
                            </Whisper>
                          </div>
                          <div className={styles['estoque-venda-bottom']}>
                            <Whisper placement="left" speaker={<Tooltip>Dias de estoque</Tooltip>}>
                              <span className={styles['estoque-venda-badge']}>
                                {rowData.stock.coverageDays ? `${Math.round(rowData.stock.coverageDays)}d` : '-'}
                              </span>
                            </Whisper>
                            <Whisper placement="right" speaker={<Tooltip>Data última entrada do produto</Tooltip>}>
                              <span className={styles['estoque-venda-data']}>
                                {rowData.stock.lastUpdated && dayjs(rowData.stock.lastUpdated).isValid()
                                  ? dayjs(rowData.stock.lastUpdated).format('DD/MM/YYYY')
                                  : '--'}
                              </span>
                            </Whisper>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Cell>
              </Column>
              <Column {...dimensoesDaColuna('margem objetiva')}>
                <HeaderCell>
                  <div>
                    <p className={styles['table-header-text']}>Margem</p>
                    <p className={styles['table-header-text']}>objetiva</p>
                  </div>
                </HeaderCell>
                <Cell dataKey="newObjectiveMargin">
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      className={styles['margem-objetiva-cell-wrapper']}
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <InputGroup>
                        <CurrencyInput
                          name="margemObjetiva"
                          decimalSeparator=","
                          precision={1}
                          // Campo vazio é "sem edição" e volta para a margem cadastrada; zero digitado
                          // aparece como 0,0 e vale como margem editada.
                          allowNull
                          allowNegative
                          value={(rowData.newObjectiveMargin ?? rowData.objectiveMargin ?? null) as number | null}
                          onChangeEvent={(_e, value) => {
                            onChangeObjectiveMargin(index, value, rowData);
                          }}
                          onBlur={(_e, details) => {
                            onBlur(rowData, index, details);
                          }}
                          disabled={disableInputs}
                        />
                        <InputAddon>%</InputAddon>
                      </InputGroup>
                    </div>
                  )}
                </Cell>
              </Column>
              {/* @ts-expect-error coluna não pode receber children */}
              <Column sortable={ordenaColuna('cost')} {...dimensoesDaColuna('custo atual')}>
                <HeaderCell>Custo atual</HeaderCell>
                <Cell dataKey="cost">
                  {(rowData: RowDataProps, index: number) => {
                    const rowCostNegotiationMode = resolveCostNegotiationModeForRow(rowData, costNegotiationMode);
                    const isEntryCostUnavailable = rowCostNegotiationMode === CostNegotiationMode.ENTRY_COST && rowData.entryCostUnavailable === true;

                    // Dado de origem indisponivel: a celula principal e o subtexto de PMZ mostram a
                    // mensagem fixa e o marcador de traco, nunca um numero calculado a partir do valor
                    // ausente -- formatDecimal nao pode ser chamado antes desta decisao.
                    if (isEntryCostUnavailable) {
                      return (
                        <div
                          className={styles['custo-atual-cell-wrapper']}
                          onContextMenu={e => {
                            handleContextMenu?.(rowData, index, e);
                          }}
                        >
                          <div className={styles['linha-com-detalhe']}>
                            <p className={classNames(styles['cell-main-text'], styles['cell-main-text--unavailable'])}>{ENTRY_COST_UNAVAILABLE_MESSAGE}</p>
                            <DetalheNoIcone speaker={renderCurrentCostTooltip(rowData, rowCostNegotiationMode, 0, 0, 0, true)} />
                          </div>

                          <p className={styles['cell-subtext']}>PMZ -- </p>
                        </div>
                      );
                    }

                    // Custo ausente e custo zero mostram traço, e não R$ 0,00: nenhum produto da base
                    // custa zero de verdade, então zero ali é sempre dado que não veio. O recuo para
                    // zero continua valendo para as contas, que não podem explodir -- só não chega
                    // mais à célula, onde viraria um número contra o qual o comprador negocia.
                    const custoAtual = getCurrentInputCostForNegotiation(rowData, rowCostNegotiationMode);
                    const custoAusente = custoAtual === null || custoAtual === undefined;
                    const currentCost = custoAtual ?? 0;
                    const currentLiquidCost = getCurrentLiquidCostForNegotiation(rowData, rowCostNegotiationMode);
                    const currentPMZ = rowCostNegotiationMode === CostNegotiationMode.ENTRY_COST ? PMZNewCostCalc(rowData, currentLiquidCost) : rowData.pmz;

                    return (
                      <div
                        className={styles['custo-atual-cell-wrapper']}
                        onContextMenu={e => {
                          handleContextMenu?.(rowData, index, e);
                        }}
                      >
                        <div className={styles['linha-com-detalhe']}>
                          <p className={styles['cell-main-text']}>{custoAusente ? 'R$ --' : `R$ ${formatDecimal(currentCost, 2)}`}</p>
                          <DetalheNoIcone speaker={renderCurrentCostTooltip(rowData, rowCostNegotiationMode, currentCost, currentLiquidCost, currentPMZ, false)} />
                        </div>

                        <p className={styles['cell-subtext']}>{custoAusente ? 'PMZ --' : `PMZ R$ ${formatDecimal(currentPMZ, 2)}`}</p>
                      </div>
                    );
                  }}
                </Cell>
              </Column>

              <Column {...dimensoesDaColuna('novo custo')}>
                <HeaderCell>Novo custo</HeaderCell>
                <Cell dataKey="newCost">
                  {(rowData: RowDataProps, index: number) => {
                    // E o modo de custo e resolvido POR LINHA, porque numa negociacao fechada cada
                    // linha carrega a regua congelada dela -- pode divergir da configuracao viva.
                    const rowCostNegotiationMode = resolveCostNegotiationModeForRow(rowData, costNegotiationMode);
                    const isEntryCostUnavailable = rowCostNegotiationMode === CostNegotiationMode.ENTRY_COST && rowData.entryCostUnavailable === true;
                    const currentCost = getCurrentInputCostForVariation(rowData, rowCostNegotiationMode);
                    const newCost = rowData.newCost ?? 0;
                    const newLiquidCost =
                      rowCostNegotiationMode === CostNegotiationMode.ENTRY_COST
                        ? calculateLiquidCostFromEntryCost(newCost, rowData.purchaseTaxPercentage)
                        : newCost;
                    // currentCost so e null quando o custo de entrada esta indisponivel -- nesse caso a
                    // variacao nao pode ser calculada, e NaN faz formatVariationValue devolver o mesmo
                    // marcador de traco que o arquivo ja usa para "valor nao disponivel".
                    const variation = currentCost === null ? NaN : calculateVariationPercentage(currentCost, newCost);
                    const PMZNewCost = rowData.PMZNewCost ?? 0;
                    const hasNewCost = rowData.newCost !== null && rowData.newCost !== undefined;

                    const content = (
                      <div
                        className={classNames(styles['novo-custo-cell-wrapper'], styles['custom-cell'])}
                        onContextMenu={e => {
                          handleContextMenu?.(rowData, index, e);
                        }}
                      >
                        <InputGroup className={styles[hasNewCost ? 'input-group-focus' : 'input-group']}>
                          <InputAddon>R$</InputAddon>
                          <CurrencyInput
                            name="novoCusto"
                            decimalSeparator=","
                            thousandSeparator="."
                            precision={2}
                            allowEmpty
                            allowNegative={false}
                            value={rowData.newCost || 0}
                            onChangeEvent={(_e, value) => {
                              onChangeNewCost(index, value, rowData);
                            }}
                            onMouseDown={event => {
                              event.currentTarget.focus();
                            }}
                            onBlur={(_e, details) => {
                              onBlur(rowData, index, details);
                            }}
                            disabled={disableInputs}
                          />
                        </InputGroup>

                        <div className={styles['novo-custo-sub-wrapper']}>
                          {/* PMZ novo custo */}
                          <p className={styles['cell-subtext']}>
                            {!hasNewCost ? 'PMZ -- ' : 'PMZ R$ '}
                            {!hasNewCost ? null : formatDecimal(PMZNewCost, 2)}
                          </p>
                          {/* Indicador de variacao suprimido por inteiro quando o custo de entrada
                              esta indisponivel -- nao ha base para comparar o novo custo com nada. */}
                          {!hasNewCost || isEntryCostUnavailable ? null : (
                            <VariationBoxIndicator positiveValueIsGood={false} className="box-indicator" value={variation} suffix="%" />
                          )}
                          {/* O detalhe da cadeia do novo custo, com as tres linhas da verba, atras do
                              icone. Sem novo custo digitado o icone NAO EXISTE -- e o que antes era
                              uma sobrescrita de `open` para forcar o detalhe fechado, e que agora e
                              simplesmente a ausencia do gesto. E o guarda de foco deixou de ser
                              necessario: passar o ponteiro num icone enquanto se digita e ato
                              deliberado, e nao efeito colateral de atravessar a celula. */}
                          {!hasNewCost ? null : (
                            <DetalheNoIcone
                              speaker={renderNewCostTooltip(rowData, rowCostNegotiationMode, newCost, newLiquidCost, PMZNewCost, isEntryCostUnavailable)}
                            />
                          )}
                        </div>
                      </div>
                    );

                    return content;
                  }}
                </Cell>
              </Column>

              {verba ? (
                <Column {...dimensoesDaColuna('verba de sell-in')}>
                  <HeaderCell>Verba de sell-in</HeaderCell>
                  <Cell dataKey="sellinAllowance">
                    {(rowData: RowDataProps, index: number) => {
                      const identidade = getRowIdentity(rowData);
                      const campo = campoDaVerbaDaLinha(rowData);

                      if (!campo) return null;

                      return (
                        <div
                          className={styles['custom-cell']}
                          onContextMenu={e => {
                            handleContextMenu?.(rowData, index, e);
                          }}
                        >
                          <VerbaDeSellInCell
                            campo={campo}
                            unidade={verba.unidadeDaLinha(identidade)}
                            onChangeUnidade={() => verba.alternarUnidade(identidade)}
                            onChangeValor={valor =>
                              verba.digitar(identidade, valor, {
                                precoDeConcorrencia: rowData.competitorsPrice,
                                novoCustoLiquido: novoCustoLiquidoDaLinha(rowData),
                              })
                            }
                            onBlur={() => onVerbaBlur?.(rowData)}
                          />
                        </div>
                      );
                    }}
                  </Cell>
                </Column>
              ) : null}
              <Column {...dimensoesDaColuna('novo preço')}>
                <HeaderCell>
                  <Whisper placement="auto" trigger="hover" speaker={<Tooltip>Calculado baseado na margem objetiva do produto e no novo custo</Tooltip>}>
                    <span>Novo preço</span>
                  </Whisper>
                </HeaderCell>
                <Cell dataKey="newPrice">
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      className={classNames(styles['novo-preco-cell-wrapper'], styles['custom-cell'])}
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <InputGroup className={styles[rowData.newPrice !== null ? 'input-group-focus' : 'input-group']}>
                        <InputAddon>R$</InputAddon>
                        <CurrencyInput
                          name="novoPreco"
                          decimalSeparator=","
                          thousandSeparator="."
                          precision={2}
                          allowEmpty
                          allowNegative={false}
                          value={rowData.newPrice || 0}
                          onChangeEvent={(_e, value) => {
                            onChangeNewPrice(index, value, rowData);
                          }}
                          onBlur={(_e, details) => {
                            onBlur(rowData, index, details);
                          }}
                          disabled={disableInputs}
                        />
                      </InputGroup>

                      <div className={styles['novo-preco-sub-wrapper']}>
                        <MoneyPercentageIndicator
                          value={rowData.newPriceMargin || null}
                          newObjectiveMargin={rowData?.newObjectiveMargin}
                          tooltip={`Margem baseada no novo custo`}
                        />
                        <Divider vertical />

                        <Whisper placement="auto" trigger="hover" speaker={<Tooltip>Competitividade do novo preço</Tooltip>}>
                          <EmojiEventsIcon className={[styles['trophy-icon'], 'margin-right'].join(' ')} />
                        </Whisper>

                        {rowData.newPriceCompetitors !== null ? (
                          <p className={styles['cell-subtext']}>{formatDecimal(rowData.newPriceCompetitors || 0, 1)} %</p>
                        ) : (
                          '--'
                        )}
                      </div>
                    </div>
                  )}
                </Cell>
              </Column>
              {/* @ts-expect-error coluna não pode receber children */}
              <Column sortable={ordenaColuna('price_retail')} {...dimensoesDaColuna('preço vigente')}>
                <HeaderCell>Preço vigente</HeaderCell>
                <Cell dataKey="price_retail">
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      className={styles['preco-vigente-cell-wrapper']}
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <p className={styles['cell-main-text']}>R$ {rowData.retailPrice === null ? '--' : formatDecimal(rowData.retailPrice, 2)}</p>

                      <div className={styles['preco-vigente-sub-wrapper']}>
                        <MoneyPercentageIndicator
                          value={rowData.marginRetailPrice || null}
                          newObjectiveMargin={rowData?.newObjectiveMargin}
                          tooltip={`Margem do preço vigente`}
                        />
                        <Divider vertical />
                        <Whisper placement="auto" trigger="hover" speaker={<Tooltip>Competitividade do preço vigente</Tooltip>}>
                          <EmojiEventsIcon className={[styles['trophy-icon'], 'margin-right'].join(' ')} />
                        </Whisper>
                        {rowData.competitorsPrice !== null && rowData.retailPrice !== null ? (
                          <p className={styles['cell-subtext']}>{formatDecimal(((rowData.retailPrice || 1) / rowData.competitorsPrice) * 100, 1)} %</p>
                        ) : (
                          '--'
                        )}
                      </div>
                    </div>
                  )}
                </Cell>
              </Column>
              {/* @ts-expect-error coluna não pode receber children */}
              <Column sortable={ordenaColuna('competitors_price')} {...dimensoesDaColuna('preço concorrência')}>
                <HeaderCell>
                  <div>
                    <p className={styles['table-header-text']}>Preço</p>
                    <p className={styles['table-header-text']}>concorrência</p>
                  </div>
                </HeaderCell>
                <Cell dataKey="competitors_price">
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      className={styles['preco-concorrencia-cell-wrapper']}
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <div className={styles.flex}>
                        {/* O travessão explicado. Medido na réplica de leitura de produção em
                            19/08/2026: 58% das linhas de negociação de cluster vivas exibem concorrência
                            vazia, e em 94% desses casos o dado existe no nível produto e falta no nível
                            cluster. Para o comprador, "não há concorrente" e "o produtor não gerou o dado"
                            eram o mesmo travessão.

                            A decisão de Cruj em 19/08/2026, entre quatro caminhos, foi manter o vazio
                            dizendo por quê — e NÃO preenchê-lo com o número de nível produto, porque isso
                            mudaria contra qual número o comprador negocia. Por isso aqui não há ramo que
                            faça número aparecer: só a explicação do vazio, e só quando a causa foi medida. */}
                        {explicacaoDoPrecoDeConcorrencia(rowData) ? (
                          <span className={styles['linha-com-detalhe']}>
                            <p className={styles['cell-main-text']}>R$ --</p>
                            <DetalheNoIcone placement="top" speaker={<Tooltip>{explicacaoDoPrecoDeConcorrencia(rowData)}</Tooltip>} />
                          </span>
                        ) : (
                          <p className={styles['cell-main-text']}>R$ {rowData.competitorsPrice !== null ? formatDecimal(rowData.competitorsPrice, 2) : '--'}</p>
                        )}
                        <span className={styles['left-icon']}>
                          {rowData?.competitivenessIndicator === null ? '' : competitivenessIndicator(rowData?.competitivenessIndicator)}
                        </span>
                      </div>

                      <div className={styles['preco-concorrencia-sub-wrapper']}>
                        <MoneyPercentageIndicator
                          value={rowData.marginCompetitorsPrice || null}
                          newObjectiveMargin={rowData?.newObjectiveMargin}
                          tooltip={`Margem do preço concorrente`}
                        />{' '}
                        {/* O botão de abrir e fechar o detalhe de concorrência é oferecido na visão
                            rede SOMENTE quando a tela sabe montar aquele detalhe.

                            O que ele custa: as duas fontes de detalhe de concorrência respondem no
                            grão de UMA loja do cliente, e a linha de rede consolida todas. Quem
                            oferece o botão nessa visão tem de buscar loja por loja e juntar; quem não
                            faz isso mostraria o detalhe de uma loja qualquer embaixo do número que
                            agrega a rede inteira, e a tela contradiria a si mesma. Por isso a decisão
                            é da tela, e chega por propriedade — a grade é a mesma nas duas telas, e
                            só uma delas tem a busca.

                            Nas visões por loja e por cluster nada mudou: a linha já é de uma loja ou
                            de um cluster, e o botão sempre existiu. */}
                        {rowData.competitorsPrice !== null && (!isNetworkExhibition || offersNetworkCompetitionDetail) ? (
                          <Button
                            className={styles['btn-expand']}
                            onClick={() => {
                              onExpandRow?.(rowData);
                            }}
                          >
                            {hasItemInList(getRowIdentity(rowData)) ? (
                              <>
                                <ExpandLessIcon className={styles['expand-icon']} />
                                Fechar
                              </>
                            ) : (
                              <>
                                <ExpandMoreIcon className={styles['expand-icon']} />
                                Abrir
                              </>
                            )}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )}
                </Cell>
              </Column>
              {/* @ts-expect-error coluna não pode receber children */}
              <Column sortable={false} {...dimensoesDaColuna('preço ideal de compra')}>
                <HeaderCell>
                  <div className={styles['preco-ideal-wrapper']}>
                    <p className={styles['table-header-text']}>Preço ideal</p>
                    <p className={styles['table-header-text']}>de compra</p>

                    <Whisper
                      placement="auto"
                      trigger="hover"
                      speaker={<Tooltip>Preço ideal de compra baseado no preço concorrência e margem objetiva</Tooltip>}
                    >
                      <img src={infoIcon} className={styles['icon-info']} alt="" />
                    </Whisper>
                  </div>
                </HeaderCell>
                <Cell dataKey="precoIdealCompra">
                  {(rowData: RowDataProps, index: number) => (
                    <div
                      onContextMenu={e => {
                        handleContextMenu?.(rowData, index, e);
                      }}
                    >
                      <BestBuyPriceIndicator data={rowData} costNegotiationMode={costNegotiationMode} isClosedNegotiation={isClosedNegotiation} />
                      <LoadingSpinerArea area={`negotiation-edit-datapoint-${getRowIdentity(rowData)}`} size="sm" height="64px" />
                    </div>
                  )}
                </Cell>
              </Column>
              {/* Conditional Actions Column - Only visible when URL contains "negociacoes/salvas" */}
              {isNegociacoesSalvasPath && (
                /* @ts-expect-error coluna não pode receber children */
                <Column {...dimensoesDaColuna('aprovação')} sortable>
                  <HeaderCell>
                    <div>
                      <MdCheck className={styles['sort-icon']} />
                    </div>
                  </HeaderCell>
                  <Cell dataKey="approved_status">
                    {(rowData: RowDataProps, index: number) => {
                      const isRowSelected = isChecked(getRowIdentity(rowData));
                      const shouldDisableButtons = disableInputs || isRowSelected;

                      return (
                        <div
                          onContextMenu={e => {
                            handleContextMenu?.(rowData, index, e);
                          }}
                        >
                          <ActionButtonsContainer rowData={rowData} disabled={shouldDisableButtons} onStatusChange={onStatusChange} />
                        </div>
                      );
                    }}
                  </Cell>
                </Column>
              )}
            </Table>
          </ContextMenuTrigger>
          {(contextMenu || verba) && contextMenuItem && (
            <ContextMenuContent>
              {/* O menu passou a abrir tambem quando SO a verba esta ligada, e nesse caso este bloco
                  nao existe -- por isso o acesso e opcional. Sem isso a grade estoura ao abrir o menu
                  numa tela que nao passa o bloco de desfazer preco. */}
              {contextMenu?.onUndoChanges && contextMenuItem?.isEdited && (
                <ContextMenuItem
                  onClick={() => {
                    contextMenu.onUndoChanges(contextMenuItem);
                  }}
                >
                  Desfazer alteração de preço
                </ContextMenuItem>
              )}
              {verba &&
                (() => {
                  // **O desfazer vale so para o que o comprador digitou**; quem aplicou a ideal
                  // volta pelo `Limpar verba`. O sufixo nomeia a origem BASE da linha.
                  const campo = campoDaVerbaDaLinha(contextMenuItem);
                  if (!campo || campo.estado === 'SEM_CONTRATO') return null;

                  const podeDesfazer = campo.selo === 'editado' && !campo.somenteLeitura;
                  const podeAplicarAIdeal = campo.verbaSugerida !== null && !campo.somenteLeitura && !campo.desabilitado;

                  // Limpar vale com qualquer origem, inclusive onde o desfazer nao vale.
                  const podeLimpar = campo.valor !== null && !campo.somenteLeitura;

                  // O rotulo e a acao saem do MESMO destino, resolvido uma vez.
                  const destino = campo.destinoDoDesfazer;

                  return (
                    <>
                      <ContextMenuLabel>{`${MENU_DA_VERBA.CABECALHO} · ${contextMenuItem.productId ?? ''}`}</ContextMenuLabel>
                      <ContextMenuItem
                        disabled={!podeDesfazer}
                        onClick={() => {
                          if (!podeDesfazer) return;

                          // Desfazer E gravar, no mesmo clique: so o estado local nao muda a tela.
                          const paraGravar = verba.desfazer(getRowIdentity(contextMenuItem), destino);

                          onVerbaBlur?.(contextMenuItem, paraGravar);
                        }}
                      >
                        {MENU_DA_VERBA.DESFAZER}
                        {podeDesfazer && destino.sufixo ? <small>{destino.sufixo}</small> : null}
                      </ContextMenuItem>
                      <ContextMenuItem
                        disabled={!podeAplicarAIdeal}
                        onClick={() => {
                          if (!podeAplicarAIdeal) return;

                          // Aplicar E gravar, no mesmo clique: quem deriva e o motor, na gravacao.
                          const paraGravar = verba.aplicarIdeal(getRowIdentity(contextMenuItem), campo.verbaSugerida);

                          if (paraGravar) onVerbaBlur?.(contextMenuItem, paraGravar);
                        }}
                      >
                        {MENU_DA_VERBA.APLICAR_IDEAL}
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        disabled={!podeLimpar}
                        onClick={() => {
                          if (!podeLimpar) return;

                          const paraGravar = verba.desfazer(getRowIdentity(contextMenuItem), LIMPAR_A_VERBA);

                          onVerbaBlur?.(contextMenuItem, paraGravar);
                        }}
                      >
                        {MENU_DA_VERBA.LIMPAR}
                      </ContextMenuItem>
                    </>
                  );
                })()}
            </ContextMenuContent>
          )}
        </ContextMenu>

        {/* Paginar um erro não faz sentido, e a tabela tem altura automática: com o corpo substituído
            pelo bloco, a paginação subiria e ficaria colada nele. Ocultá-la é uma resposta
            PROVISÓRIA a uma pergunta visual que o material desta entrega não responde — o material
            descreve o estado de erro ora como aviso flutuante, ora como faixa, e nenhum dos dois
            desenha o bloco na área do corpo. Quem tiver a resposta muda esta linha. */}
        {!isError && <Pagination {...pagination} />}

        {/* Stores Modal */}
        <StoresModal
          open={showClusterModal}
          onClose={handleCloseClusterModal}
          selectedRowData={selectedRowData}
          costNegotiationMode={costNegotiationMode}
          // A grade já lê o agrupamento escolhido do estado global, e é ela que o repassa ao modal.
          // O modal não o lê por conta própria de propósito: assim ele é testável isolado, e a
          // visão que viaja no corpo da requisição tem uma origem só.
          suppliersExhibitionType={suppliersExhibitionType}
        />
      </div>
    );
  },
);

TableFornecedor.displayName = 'TableFornecedor';

export { TableFornecedor };
